import { useEffect, useLayoutEffect, useRef, useState, type ReactNode } from 'react';
import { ChevronRight } from 'lucide-react';

// A menu entry. Either a divider or an actionable/submenu item.
export type MenuNode =
  | { divider: true }
  | {
      id: string;
      label: string;
      icon?: ReactNode;
      shortcut?: string;
      danger?: boolean;
      disabled?: boolean;
      checked?: boolean;
      onSelect?: () => void;
      submenu?: MenuNode[];
    };

interface ContextMenuProps {
  x: number;
  y: number;
  items: MenuNode[];
  onClose: () => void;
}

// Cursor-positioned context menu with arbitrarily nested submenus. Closes on
// outside click, Escape, scroll, or window resize.
export function ContextMenu({ x, y, items, onClose }: ContextMenuProps) {
  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape') onClose();
    }
    document.addEventListener('keydown', onKey);
    window.addEventListener('resize', onClose);
    window.addEventListener('wheel', onClose, { passive: true });
    return () => {
      document.removeEventListener('keydown', onKey);
      window.removeEventListener('resize', onClose);
      window.removeEventListener('wheel', onClose);
    };
  }, [onClose]);

  return (
    <div
      className="fixed inset-0 z-50"
      onMouseDown={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
      onContextMenu={(e) => {
        e.preventDefault();
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <MenuPanel x={x} y={y} items={items} onClose={onClose} />
    </div>
  );
}

function MenuPanel({
  x,
  y,
  items,
  onClose,
}: {
  x: number;
  y: number;
  items: MenuNode[];
  onClose: () => void;
}) {
  const ref = useRef<HTMLDivElement | null>(null);
  const [openId, setOpenId] = useState<string | null>(null);
  const [pos, setPos] = useState({ left: x, top: y });

  // Clamp the panel inside the viewport once its size is known.
  useLayoutEffect(() => {
    const el = ref.current;
    if (!el) return;
    const r = el.getBoundingClientRect();
    let left = x;
    let top = y;
    if (left + r.width > window.innerWidth) left = Math.max(4, x - r.width);
    if (left + r.width > window.innerWidth) left = Math.max(4, window.innerWidth - r.width - 4);
    if (top + r.height > window.innerHeight) top = Math.max(4, window.innerHeight - r.height - 4);
    setPos({ left, top });
  }, [x, y, items]);

  return (
    <div
      ref={ref}
      style={{ left: pos.left, top: pos.top }}
      className="fixed min-w-[210px] bg-white border border-gray-200 rounded-md shadow-lg py-1 text-sm"
    >
      {items.map((it, i) =>
        'divider' in it ? (
          <div key={`d${i}`} className="my-1 border-t border-gray-100" />
        ) : (
          <MenuRow
            key={it.id}
            item={it}
            open={openId === it.id}
            onOpen={() => setOpenId(it.id)}
            onClose={onClose}
          />
        )
      )}
    </div>
  );
}

function MenuRow({
  item,
  open,
  onOpen,
  onClose,
}: {
  item: Extract<MenuNode, { id: string }>;
  open: boolean;
  onOpen: () => void;
  onClose: () => void;
}) {
  const rowRef = useRef<HTMLButtonElement | null>(null);
  const hasSub = !!item.submenu && item.submenu.length > 0;
  const [subPos, setSubPos] = useState<{ x: number; y: number } | null>(null);

  function openSub() {
    const r = rowRef.current?.getBoundingClientRect();
    if (r) setSubPos({ x: r.right - 3, y: r.top - 5 });
    onOpen();
  }

  return (
    <div className="relative" onMouseEnter={hasSub ? openSub : onOpen}>
      <button
        ref={rowRef}
        type="button"
        disabled={item.disabled}
        onClick={() => {
          if (item.disabled) return;
          if (hasSub) {
            openSub();
            return;
          }
          item.onSelect?.();
          onClose();
        }}
        className={`w-full flex items-center gap-2 px-3 py-1.5 text-left ${
          item.danger ? 'text-red-600' : 'text-gray-700'
        } ${item.disabled ? 'opacity-40 cursor-not-allowed' : 'hover:bg-gray-50'}`}
      >
        <span className="w-4 flex-shrink-0 flex justify-center text-gray-500">
          {item.checked ? '✓' : item.icon}
        </span>
        <span className="flex-1 truncate">{item.label}</span>
        {item.shortcut && <span className="text-[10px] text-gray-400 ml-2">{item.shortcut}</span>}
        {hasSub && <ChevronRight className="w-3.5 h-3.5 text-gray-400 flex-shrink-0" />}
      </button>
      {hasSub && open && subPos && (
        <MenuPanel x={subPos.x} y={subPos.y} items={item.submenu!} onClose={onClose} />
      )}
    </div>
  );
}
